import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Factory,
  Plus,
  Eye,
  Settings2,
  CheckCircle2,
  XCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import {
  useProductionRuns,
  useMachines,
  useCreateRun,
  useCreateMachine,
  useProductionRun,
  useAddRunInput,
  useAddRunOutput,
  useCompleteRun,
  useCancelRun,
  ListRunsParams,
} from "../api/production";
import { useProducts } from "../api/products";
import { useAuth } from "../context/AuthContext";
import { ProductionRun, ProductionStatus, Shift } from "../types/models";
import { PageHeader } from "../components/ui/PageHeader";
import { Pagination } from "../components/ui/Pagination";
import { StatusBadge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { Button, SelectField, TextField, TextareaField } from "../components/ui/Form";
import { LoadingState, ErrorState, EmptyState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { formatNumber, humanizeEnum } from "../utils/helpers";

const ACTIVE_STATUSES: ProductionStatus[] = [
  ProductionStatus.DRAFT,
  ProductionStatus.IN_PROGRESS,
];

export const ProductionPage: React.FC = () => {
  const { isManagement } = useAuth();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ProductionStatus | "">("");
  const [shift, setShift] = useState<Shift | "">("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [machinesOpen, setMachinesOpen] = useState(false);

  const params: ListRunsParams = {
    page,
    limit: 10,
    status: status || undefined,
    shift: shift || undefined,
  };
  const { data, isLoading, isError, error, refetch, isFetching } = useProductionRuns(params);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Production"
        subtitle="Conversion runs, yield and wastage tracking."
        actions={
          <>
            {isManagement && (
              <Button variant="secondary" icon={<Settings2 size={18} />} onClick={() => setMachinesOpen(true)}>
                Machines
              </Button>
            )}
            <Button icon={<Plus size={18} />} onClick={() => setCreating(true)}>
              New Run
            </Button>
          </>
        }
      />

      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <select value={status} onChange={(e) => { setStatus(e.target.value as ProductionStatus | ""); setPage(1); }} className="input sm:w-52">
          <option value="">All statuses</option>
          {Object.values(ProductionStatus).map((s) => (
            <option key={s} value={s}>{humanizeEnum(s)}</option>
          ))}
        </select>
        <select value={shift} onChange={(e) => { setShift(e.target.value as Shift | ""); setPage(1); }} className="input sm:w-44">
          <option value="">All shifts</option>
          {Object.values(Shift).map((s) => (
            <option key={s} value={s}>{humanizeEnum(s)}</option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={<Factory size={32} />} title="No production runs" description="Create a run to start tracking conversion." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Run</th>
                    <th className="px-4 py-3">Machine</th>
                    <th className="px-4 py-3">Shift</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Output (kg)</th>
                    <th className="px-4 py-3 text-right">Yield</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className={isFetching ? "opacity-60 transition-opacity" : ""}>
                  {data?.items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800 last:border-0 hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-medium text-slate-100">{r.runNumber}</td>
                      <td className="px-4 py-3 text-slate-300">{r.machine?.name ?? "Unassigned"}</td>
                      <td className="px-4 py-3 text-slate-300">{humanizeEnum(r.shift)}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 text-right text-slate-200">{formatNumber(r.totalOutputQty)}</td>
                      <td className="px-4 py-3 text-right text-slate-200">{formatNumber(r.yieldPercent, 1)}%</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button onClick={() => setDetailId(r.id)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-slate-300 hover:bg-slate-700 hover:text-slate-100">
                            <Eye size={16} /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data?.meta} onPageChange={setPage} />
          </>
        )}
      </div>

      {detailId && <RunDetailModal runId={detailId} onClose={() => setDetailId(null)} />}
      {creating && <CreateRunModal onClose={() => setCreating(false)} />}
      {machinesOpen && <MachinesModal onClose={() => setMachinesOpen(false)} />}
    </div>
  );
};

// ───────────────────────── Run detail ─────────────────────────

const RunDetailModal: React.FC<{ runId: string; onClose: () => void }> = ({ runId, onClose }) => {
  const { data: run, isLoading, isError, error, refetch } = useProductionRun(runId);
  const products = useProducts({ limit: 100 });
  const addInput = useAddRunInput();
  const addOutput = useAddRunOutput();
  const complete = useCompleteRun();
  const cancel = useCancelRun();

  const [inputForm, setInputForm] = useState({ productId: "", quantity: "" });
  const [outputForm, setOutputForm] = useState({ productId: "", quantity: "" });

  const editable = run ? ACTIVE_STATUSES.includes(run.status) : false;
  const catalogue = products.data?.items ?? [];

  const submitLine = async (
    kind: "input" | "output",
    form: { productId: string; quantity: string },
    reset: () => void,
  ) => {
    if (!form.productId || !Number(form.quantity)) {
      toast.error("Select a product and quantity");
      return;
    }
    const payload = { id: runId, input: { productId: form.productId, quantity: Number(form.quantity) } };
    try {
      if (kind === "input") await addInput.mutateAsync(payload);
      else await addOutput.mutateAsync(payload);
      reset();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const doComplete = async () => {
    try {
      await complete.mutateAsync(runId);
      toast.success("Run completed");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const doCancel = async () => {
    try {
      await cancel.mutateAsync(runId);
      toast.success("Run cancelled");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={run ? `Run ${run.runNumber}` : "Production Run"}
      description={run ? `${run.machine?.name ?? "Unassigned"} · ${humanizeEnum(run.shift)} shift` : undefined}
      footer={
        editable ? (
          <div className="flex w-full items-center justify-between">
            <Button variant="danger" icon={<XCircle size={16} />} onClick={doCancel} loading={cancel.isPending}>
              Cancel Run
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Close</Button>
              <Button variant="success" icon={<CheckCircle2 size={16} />} onClick={doComplete} loading={complete.isPending}>
                Complete Run
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" onClick={onClose}>Close</Button>
        )
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : run ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-3">
            <Metric label="Status" value={<StatusBadge status={run.status} />} />
            <Metric label="Input" value={`${formatNumber(run.totalInputQty)} kg`} />
            <Metric label="Output" value={`${formatNumber(run.totalOutputQty)} kg`} />
            <Metric label="Wastage" value={`${formatNumber(run.wastageQty)} kg`} />
            <Metric label="Yield" value={`${formatNumber(run.yieldPercent, 1)}%`} />
          </div>

          <LineSection
            title="Inputs (raw materials)"
            icon={<ArrowDownToLine size={16} className="text-amber-400" />}
            lines={run.inputs ?? []}
          />
          {editable && (
            <LineForm
              catalogue={catalogue}
              form={inputForm}
              setForm={setInputForm}
              loading={addInput.isPending}
              onSubmit={() => submitLine("input", inputForm, () => setInputForm({ productId: "", quantity: "" }))}
            />
          )}

          <LineSection
            title="Outputs (finished goods)"
            icon={<ArrowUpFromLine size={16} className="text-emerald-400" />}
            lines={run.outputs ?? []}
          />
          {editable && (
            <LineForm
              catalogue={catalogue}
              form={outputForm}
              setForm={setOutputForm}
              loading={addOutput.isPending}
              onSubmit={() => submitLine("output", outputForm, () => setOutputForm({ productId: "", quantity: "" }))}
            />
          )}

          {run.notes && (
            <p className="rounded-lg bg-slate-900/40 p-3 text-sm text-slate-300">
              <span className="font-medium text-slate-200">Notes: </span>{run.notes}
            </p>
          )}
        </div>
      ) : null}
    </Modal>
  );
};

const Metric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg bg-slate-900/40 px-4 py-2">
    <div className="text-xs text-slate-400">{label}</div>
    <div className="mt-0.5 font-semibold text-slate-100">{value}</div>
  </div>
);

const LineSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  lines: ProductionRun["inputs"];
}> = ({ title, icon, lines }) => (
  <div>
    <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-200">{icon} {title}</h4>
    {!lines || lines.length === 0 ? (
      <p className="text-sm text-slate-500">None recorded.</p>
    ) : (
      <div className="space-y-1">
        {lines.map((l) => (
          <div key={l.id} className="flex justify-between rounded-lg bg-slate-900/30 px-3 py-1.5 text-sm">
            <span className="text-slate-300">{l.product?.name ?? "Product"}</span>
            <span className="text-slate-200">{formatNumber(l.quantity, 3)} {humanizeEnum(l.product?.unit)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const LineForm: React.FC<{
  catalogue: { id: string; name: string; sku: string }[];
  form: { productId: string; quantity: string };
  setForm: (f: { productId: string; quantity: string }) => void;
  loading: boolean;
  onSubmit: () => void;
}> = ({ catalogue, form, setForm, loading, onSubmit }) => (
  <div className="flex items-end gap-2">
    <div className="flex-1">
      <select className="input" value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value })}>
        <option value="">Select product…</option>
        {catalogue.map((p) => (
          <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
        ))}
      </select>
    </div>
    <input
      type="number"
      step="0.001"
      min={0}
      placeholder="Qty"
      className="input w-28"
      value={form.quantity}
      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
    />
    <Button type="button" variant="secondary" icon={<Plus size={16} />} onClick={onSubmit} loading={loading}>
      Add
    </Button>
  </div>
);

// ───────────────────────── Create run ─────────────────────────

const runSchema = z.object({
  machineId: z.string().optional(),
  shift: z.nativeEnum(Shift),
  notes: z.string().optional(),
});
type RunValues = z.infer<typeof runSchema>;

const CreateRunModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const machines = useMachines();
  const create = useCreateRun();
  const { register, handleSubmit } = useForm<RunValues>({
    resolver: zodResolver(runSchema),
    defaultValues: { shift: Shift.MORNING },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const run = await create.mutateAsync({
        machineId: values.machineId || undefined,
        shift: values.shift,
        notes: values.notes || undefined,
      });
      toast.success(`Run ${run.runNumber} created`);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="New Production Run"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={create.isPending}>Create run</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <SelectField label="Machine" {...register("machineId")}>
          <option value="">Unassigned</option>
          {machines.data?.filter((m) => m.isActive).map((m) => (
            <option key={m.id} value={m.id}>{m.name} ({m.code})</option>
          ))}
        </SelectField>
        <SelectField label="Shift" {...register("shift")}>
          {Object.values(Shift).map((s) => (
            <option key={s} value={s}>{humanizeEnum(s)}</option>
          ))}
        </SelectField>
        <TextareaField label="Notes" {...register("notes")} />
      </form>
    </Modal>
  );
};

// ───────────────────────── Machines ─────────────────────────

const machineSchema = z.object({
  name: z.string().min(2, "Name is required"),
  code: z.string().min(2, "Code is required"),
});
type MachineValues = z.infer<typeof machineSchema>;

const MachinesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const machines = useMachines();
  const create = useCreateMachine();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<MachineValues>({
    resolver: zodResolver(machineSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await create.mutateAsync(values);
      toast.success("Machine added");
      reset();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Machines" size="md" footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="space-y-4">
        <div className="space-y-1.5">
          {machines.data?.length ? (
            machines.data.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-lg bg-slate-900/40 px-3 py-2 text-sm">
                <span className="text-slate-200">{m.name} <span className="text-slate-500">({m.code})</span></span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${m.isActive ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>
                  {m.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">No machines yet.</p>
          )}
        </div>
        <form onSubmit={onSubmit} className="flex items-end gap-2 border-t border-slate-700 pt-4">
          <TextField label="Name" className="flex-1" error={errors.name?.message} {...register("name")} />
          <TextField label="Code" className="w-32" error={errors.code?.message} {...register("code")} />
          <Button type="submit" icon={<Plus size={16} />} loading={create.isPending}>Add</Button>
        </form>
      </div>
    </Modal>
  );
};
